import { supabase } from '../api/supabase';

/**
 * Utility function to sync vehicle status based on current driver assignment
 * This helps prevent status/assignment inconsistencies
 */
export const syncVehicleStatus = async (vehicleId: string): Promise<void> => {
  try {
    // Get current vehicle state
    const { data: vehicle, error: vehicleError } = await supabase
      .from('vehicles')
      .select('id, car_status, current_driver_id, license_plate')
      .eq('id', vehicleId)
      .single();

    if (vehicleError || !vehicle) {
      console.error('Error fetching vehicle for status sync:', vehicleError);
      return;
    }

    // Check if vehicle has active job card (takes priority over everything)
    const { data: activeJobCard } = await supabase
      .from('job_cards')
      .select('id')
      .eq('vehicle_id', vehicleId)
      .is('return_date', null)
      .limit(1)
      .single();

    // If vehicle has active job card, status should be in_workshop
    if (activeJobCard) {
      if (vehicle.car_status !== 'in_workshop') {
        console.log(`🔧 Vehicle ${vehicle.license_plate} has active job card - setting to in_workshop`);
        const { error: updateError } = await supabase
          .from('vehicles')
          .update({ car_status: 'in_workshop' })
          .eq('id', vehicleId);
        
        if (!updateError) {
          console.log(`✅ Vehicle ${vehicle.license_plate} status synced to in_workshop`);
        }
      }
      return; // Don't apply other status logic if vehicle is in workshop
    }

    // Determine correct status based on driver assignment (only if NOT in workshop)
    const shouldBeWorking = vehicle.current_driver_id !== null;
    const currentStatus = vehicle.car_status;
    
    let targetStatus: string;
    
    if (shouldBeWorking) {
      // Vehicle has a driver - should be 'work'
      targetStatus = 'work';
    } else {
      // Vehicle has no driver - should be 'available' unless specifically stopped/cancelled
      targetStatus = ['stop', 'cancellation'].includes(currentStatus) 
        ? currentStatus 
        : 'available';
    }

    // Update status if it doesn't match
    if (currentStatus !== targetStatus) {
      console.log(`🔄 Syncing vehicle ${vehicle.license_plate}: ${currentStatus} → ${targetStatus}`);
      
      const { error: updateError } = await supabase
        .from('vehicles')
        .update({ car_status: targetStatus })
        .eq('id', vehicleId);

      if (updateError) {
        console.error('Error updating vehicle status:', updateError);
      } else {
        console.log(`✅ Vehicle ${vehicle.license_plate} status synced to ${targetStatus}`);
      }
    }
  } catch (error) {
    console.error('Error in syncVehicleStatus:', error);
  }
};

/**
 * Sync all vehicles' statuses - useful for fixing bulk inconsistencies
 */
export const syncAllVehicleStatuses = async (): Promise<void> => {
  try {
    console.log('🔄 Starting bulk vehicle status sync...');
    
    const { data: vehicles, error } = await supabase
      .from('vehicles')
      .select('id, car_status, current_driver_id, license_plate');

    if (error || !vehicles) {
      console.error('Error fetching vehicles for bulk sync:', error);
      return;
    }

    // Get all active job cards
    const { data: activeJobCards } = await supabase
      .from('job_cards')
      .select('vehicle_id')
      .is('return_date', null);

    const vehiclesInWorkshop = new Set(activeJobCards?.map(jc => jc.vehicle_id) || []);

    let updatedCount = 0;
    
    for (const vehicle of vehicles) {
      let targetStatus: string;
      
      // Priority 1: If vehicle has active job card, it should be in_workshop
      if (vehiclesInWorkshop.has(vehicle.id)) {
        targetStatus = 'in_workshop';
      } else {
        // Priority 2: Status based on driver assignment
        const shouldBeWorking = vehicle.current_driver_id !== null;
        const currentStatus = vehicle.car_status;
        
        if (shouldBeWorking) {
          targetStatus = 'work';
        } else {
          targetStatus = ['stop', 'cancellation'].includes(currentStatus) 
            ? currentStatus 
            : 'available';
        }
      }

      if (vehicle.car_status !== targetStatus) {
        console.log(`🔄 Fixing vehicle ${vehicle.license_plate}: ${vehicle.car_status} → ${targetStatus}`);
        
        const { error: updateError } = await supabase
          .from('vehicles')
          .update({ car_status: targetStatus })
          .eq('id', vehicle.id);

        if (!updateError) {
          updatedCount++;
        }
      }
    }
    
    console.log(`✅ Bulk sync complete: ${updatedCount} vehicles updated`);
  } catch (error) {
    console.error('Error in syncAllVehicleStatuses:', error);
  }
};
